/**
 * RoundedSearchInput - Pill-shaped search input matching SPOC tasks filter bar style.
 * Reusable across Overview tabs (Tasks, Opportunities, Patients) and SPOC.
 */

import React, { useState } from "react";
import {
  colorTokens,
  getSpacing,
  getTypography,
  typographyTokens,
} from "../../../designTokens";
import searchIcon from "../../../Assets/icons/Search.svg";

export interface RoundedSearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  id: string;
  "aria-label"?: string;
  /** Container width (default 200px for SPOC; use e.g. "400px" for Overview) */
  width?: string;
  /** Optional container id for the wrapper (e.g. for testing) */
  containerId?: string;
}

const RoundedSearchInput: React.FC<RoundedSearchInputProps> = ({
  value,
  onChange,
  placeholder = "Search...",
  id,
  "aria-label": ariaLabel = "Search",
  width = "200px",
  containerId,
}) => {
  const [focused, setFocused] = useState(false);

  return (
    <div
      id={containerId ?? undefined}
      style={{
        display: "flex",
        alignItems: "center",
        gap: getSpacing("8px"),
        padding: `${getSpacing("4px")} ${getSpacing("12px")}`,
        borderRadius: "20px",
        border: `1px solid ${
          focused
            ? colorTokens["Primary/Default"]
            : colorTokens["Borders/Light Grey"]
        }`,
        backgroundColor: colorTokens["Background/White"],
        width,
        height: "32px",
        boxSizing: "border-box",
        transition: "border-color 0.2s ease",
      }}
    >
      <img
        src={searchIcon}
        alt=""
        aria-hidden="true"
        style={{ width: "14px", height: "14px", flexShrink: 0 }}
      />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={placeholder}
        aria-label={ariaLabel}
        id={id}
        style={{
          flex: 1,
          minWidth: 0,
          border: "none",
          outline: "none",
          ...getTypography("Body/Medium"),
          fontSize: `${typographyTokens["Body/Medium"].size}px`,
          color: colorTokens["Text/Black"],
          backgroundColor: "transparent",
        }}
      />
    </div>
  );
};

export default RoundedSearchInput;
